// Adding a task by hand. The dialog stays small on purpose: title, owners,
// what it is about, target date, priority. Everything else is edited on the row
// afterwards, because a long form at the moment of capture is how a task board
// ends up empty.
//
// TWO THINGS IT DELIBERATELY DOES NOT ASK
//
// There is no status field. A task nobody has started is Not Started, and since
// db/migrations/0023 the group follows the status, so a new task lands in "Not
// started" whichever heading the button was pressed on. The dialog says which
// group it will land in rather than letting that be a surprise. Standing and On
// Hold are the exceptions and keep the group they were given.
//
// There is no free-text vehicle field. Registration numbers on the old board
// were written six different ways and three of the plates in the meeting notes
// match nothing at all. The links are foreign keys or they are nothing.
//
// WHAT WAS ADDED ON 27 AUGUST 2026, AND THE ONE AWKWARDNESS IN IT
//
// Luyanda asked to be able to attach a document while creating a task. Until now
// a file could only be added to a task that already existed, which meant opening
// the row again after saving, and the file most worth attaching is the one in
// your hand at the moment you are typing the task.
//
// The awkwardness is real and is not hidden: A FILE CANNOT BE UPLOADED BEFORE THE
// TASK EXISTS. The signed upload URL is composed by the Worker from the task's
// id, which is the entire security model of the attachment path, so there is no
// id to sign against until the row is written. Three ways out were considered:
//
//   1. Reserve an id in the browser and write the row later. Rejected: a client
//      that chooses its own primary key can write into another task's folder.
//   2. Upload to a staging folder and move the objects after creation. Rejected:
//      it needs a mover with delete rights on a bucket where the standing rule is
//      that nothing can be removed.
//   3. Hold the files in the dialog, create the task, then upload. Chosen.
//
// So the button creates and then uploads, and the failure mode is stated rather
// than swallowed: if the task saves and a file does not, the task is kept and the
// message names the file. The alternative, rolling the task back because an
// upload failed, would throw away what somebody typed to protect a screenshot
// they can add in two clicks.
//
// The authority level is here too, and it is allowed to be left unset. It is the
// one field on this dialog that the meeting ingestion is forbidden from filling
// in; see db/migrations/0085.
import { useRef, useState } from 'react'
import { Paperclip, Upload, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  useCreateTask,
  useRecordTaskAsset,
  useSetTaskLinks,
  useSignTaskUpload,
} from '../../hooks/backend/tasks'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Textarea } from '../../lib/shadcn/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../lib/shadcn/dialog'
import {
  AUTHORITY_HINT,
  AUTHORITY_OPTIONS,
  GROUP_LABEL,
  PRIORITY_OPTIONS,
  groupForStatus,
  type BoardGroup,
  type LinkKind,
  type TaskAuthority,
  type TaskLinkOption,
  type TaskPerson,
} from '../../utils/tasks'
import { formatFileSize } from '../../utils/documents'
import { MultiSelect, type Choice } from './TaskPickers'
import { TASK_FILE_MAX_BYTES, oversizeFiles, uploadTaskFiles } from './uploadTaskFiles'

const LINK_SECTION: Record<LinkKind, string> = {
  vehicle: 'Vehicles',
  owner: 'Vehicle owners',
  driver: 'Drivers',
}

export function NewTaskDialog({
  open,
  group,
  people,
  linkOptions,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  group: BoardGroup
  people: TaskPerson[]
  linkOptions: TaskLinkOption[]
  onOpenChange: (open: boolean) => void
  onCreated: () => void
}) {
  const create = useCreateTask()
  const setLinks = useSetTaskLinks()
  const sign = useSignTaskUpload()
  const record = useRecordTaskAsset()
  const inputRef = useRef<HTMLInputElement>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [owners, setOwners] = useState<string[]>([])
  const [links, setLinkKeys] = useState<string[]>([])
  const [priority, setPriority] = useState('')
  const [authority, setAuthority] = useState('')
  const [target, setTarget] = useState('')
  // Held in the browser until the task exists. See the header for why there is
  // no way around that.
  const [files, setFiles] = useState<File[]>([])
  const [saving, setSaving] = useState(false)

  const landsIn = groupForStatus('Not Started', group)

  const reset = () => {
    setTitle('')
    setDescription('')
    setOwners([])
    setLinkKeys([])
    setPriority('')
    setAuthority('')
    setTarget('')
    setFiles([])
    if (inputRef.current) inputRef.current.value = ''
  }

  // Checked when the file is chosen, not when the task is saved. A cap enforced
  // after creation means a refusal that arrives with a task already on the board,
  // which reads as a failure that half worked.
  const chooseFiles = (chosen: FileList | null) => {
    if (!chosen || chosen.length === 0) return
    const picked = Array.from(chosen)
    const tooBig = oversizeFiles(picked)
    if (tooBig.length > 0) {
      toast.error('That file is too large', {
        description: `${tooBig.map((f) => f.name).join(', ')} is over ${Math.round(TASK_FILE_MAX_BYTES / (1024 * 1024))} MB. Compress it, or put a large file in Documents and link to it from an update.`,
      })
    }
    const keep = picked.filter((f) => f.size <= TASK_FILE_MAX_BYTES)
    // Same name twice is almost always the same file chosen twice, and two
    // objects with one name on one task is a list nobody can read.
    setFiles((prev) => [
      ...prev,
      ...keep.filter((f) => !prev.some((p) => p.name === f.name && p.size === f.size)),
    ])
    if (inputRef.current) inputRef.current.value = ''
  }

  const ownerChoices: Choice[] = people.map((p) => ({
    value: p.name,
    label: p.name,
    hint: p.has_login ? undefined : 'no login',
    sub: p.open_tasks > 0 ? `${p.open_tasks} open, ${p.overdue_tasks} overdue` : 'nothing open',
  }))

  const linkChoices: Choice[] = linkOptions.map((o) => ({
    value: `${o.kind}:${o.id}`,
    label: o.label,
    sub: o.sub_label,
    inactive: !o.is_active,
    hint: o.is_active ? undefined : (o.status ?? undefined),
    section: LINK_SECTION[o.kind],
  }))

  const submit = async () => {
    if (!title.trim()) {
      toast.error('A task needs a title before it can be saved.')
      return
    }
    setSaving(true)
    try {
      const created = await create.trigger({
        title,
        description: description || null,
        priority: priority || null,
        authority_level: authority || null,
        board_group: group,
        target_completion: target || null,
        assignees: owners,
      })

      // The links are a second call because the task has to exist before
      // anything can point at it. If a link fails the task is still saved and
      // the message says so, rather than the operator losing what they typed.
      const id = (created as { id?: string } | null)?.id
      if (id) {
        for (const kind of ['vehicle', 'owner', 'driver'] as LinkKind[]) {
          const ids = links
            .filter((k) => k.startsWith(`${kind}:`))
            .map((k) => k.slice(kind.length + 1))
          if (ids.length > 0) await setLinks.trigger({ task_id: id, kind, ids })
        }
      } else if (links.length > 0) {
        toast.error('The task was saved, but its links were not. Add them on the row.')
      }

      // Then the files, for the same reason and with the same rule: the task is
      // never rolled back because an upload failed. Counted one at a time so a
      // batch where one file fails reports the truth.
      let fileNote = ''
      if (files.length > 0) {
        if (!id) {
          fileNote = ' The files were not attached, because the server did not say which task was created. Open the task and add them.'
        } else {
          const { done, failed } = await uploadTaskFiles({
            taskId: id,
            files,
            sign: (params) => sign.trigger(params).result as Promise<unknown>,
            record: (params) => record.trigger(params).result as Promise<unknown>,
          })
          if (failed.length > 0) {
            // A separate, louder message. Rolling this into the success toast is
            // how somebody closes the dialog believing a claim form is attached.
            toast.error(
              done === 0
                ? 'The task was added. None of the files were.'
                : `The task was added with ${done} of ${files.length} files.`,
              { description: failed.join(' | ') },
            )
          } else {
            fileNote = done === 1 ? ' One file attached.' : ` ${done} files attached.`
          }
        }
      }

      reset()
      onOpenChange(false)
      onCreated()
      toast.success(`Task added.${fileNote}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'The task was not saved.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Scrolls, and it has to. Authority level and Documents added about 200px
          on 27 August 2026, which put the Add task button below the fold of a
          768px laptop and well off a phone. Three of the six people who use this
          board work from a phone, and a form whose save button cannot be reached
          is not a form. */}
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New task in {GROUP_LABEL[landsIn]}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="task-title">What needs doing</Label>
            <Input
              id="task-title"
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Renew the licence disc for MJ47JFGP"
            />
            {landsIn !== group && (
              <p className="mt-1 text-xs text-muted-foreground">
                You pressed Add on {GROUP_LABEL[group]}. A task nobody has started belongs in{' '}
                {GROUP_LABEL[landsIn]}, and it will move to {GROUP_LABEL[group]} as soon as somebody
                sets the status.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Owners</Label>
              <MultiSelect
                choices={ownerChoices}
                selected={owners}
                title="Who owns this"
                searchPlaceholder="Search people"
                onCommit={async (next) => setOwners(next)}
              >
                <button
                  type="button"
                  className="flex h-9 w-full items-center truncate rounded-md border border-input bg-background px-3 text-left text-sm"
                >
                  {owners.length === 0 ? (
                    <span className="text-muted-foreground">Unassigned</span>
                  ) : (
                    owners.join(', ')
                  )}
                </button>
              </MultiSelect>
            </div>
            <div>
              <Label htmlFor="task-priority">Priority</Label>
              <NativeSelect
                id="task-priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                <option value="">None</option>
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>

          {/* Hassan Osman's fourth step. Left as Not set rather than defaulted to
              anything: a default here would be the dialog deciding on somebody
              else's behalf how much authority they have, which is the exact
              assumption the field exists to stop. The sentence under it changes
              with the choice, because the words are the useful part. */}
          <div>
            <Label htmlFor="task-authority">Authority level</Label>
            <NativeSelect
              id="task-authority"
              value={authority}
              onChange={(e) => setAuthority(e.target.value)}
            >
              <option value="">Not set</option>
              {AUTHORITY_OPTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </NativeSelect>
            <p className="mt-1 text-xs text-muted-foreground">
              {authority
                ? AUTHORITY_HINT[authority as TaskAuthority]
                : 'How much authority the owner has. Leaving it unset is allowed, and the board will show the delegation as incomplete rather than assume an answer.'}
            </p>
          </div>

          <div>
            <Label>What it is about</Label>
            <MultiSelect
              choices={linkChoices}
              selected={links}
              title="Vehicles, owners and drivers"
              searchPlaceholder="Search by registration or name"
              onCommit={async (next) => setLinkKeys(next)}
            >
              <button
                type="button"
                className="flex h-9 w-full items-center truncate rounded-md border border-input bg-background px-3 text-left text-sm"
              >
                {links.length === 0 ? (
                  <span className="text-muted-foreground">
                    Link a vehicle, an owner or a driver
                  </span>
                ) : (
                  `${links.length} linked`
                )}
              </button>
            </MultiSelect>
          </div>

          <div>
            <Label htmlFor="task-target">Target completion</Label>
            <Input
              id="task-target"
              type="date"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Leaving this blank is allowed. The board will show the task as having no target
              rather than pretending it is on time.
            </p>
          </div>

          <div>
            <Label htmlFor="task-notes">Notes</Label>
            <Textarea
              id="task-notes"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Anything the person picking this up will need."
              className="min-h-[64px]"
            />
          </div>

          {/* FILES. Chosen here, uploaded after the task is created; the header
              of this file explains why that order is forced.

              The visibility sentence is repeated from the panel deliberately.
              Everyone who can see this board can open everything attached to it,
              which is all ten cockpit logins, and the moment somebody chooses a
              file is the only moment that fact can change their mind. */}
          <div>
            <Label>Documents</Label>
            <div className="mt-1 flex items-center gap-2">
              <input
                ref={inputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => chooseFiles(e.target.files)}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 gap-1.5 text-xs"
                disabled={saving}
                onClick={() => inputRef.current?.click()}
              >
                <Upload className="h-3.5 w-3.5" />
                Choose a file
              </Button>
              {files.length > 0 && (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Paperclip className="h-3.5 w-3.5" />
                  {files.length} to attach
                </span>
              )}
            </div>

            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f) => (
                  <li
                    key={`${f.name}:${f.size}`}
                    className="flex items-center gap-2 rounded-md border border-border px-2 py-1"
                  >
                    <span className="min-w-0 flex-1 truncate text-xs">{f.name}</span>
                    <span className="shrink-0 text-[11px] text-muted-foreground">
                      {formatFileSize(f.size)}
                    </span>
                    {/* Removable HERE and nowhere afterwards. Nothing can be
                        taken off a task once it is uploaded, which is why being
                        able to change your mind before that matters. */}
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => setFiles((prev) => prev.filter((p) => p !== f))}
                      title="Take this off the list"
                      className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <p className="mt-1 text-xs text-muted-foreground">
              Anyone who can see this board can open these files, and nothing can be removed once
              the task is saved. A driver's ID, payslip or bank letter belongs in Documents, not on
              a task.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" disabled={saving} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={saving || create.loading} onClick={() => void submit()}>
            {saving
              ? files.length > 0
                ? 'Adding and uploading'
                : 'Adding'
              : files.length > 0
                ? `Add task and ${files.length} file${files.length === 1 ? '' : 's'}`
                : 'Add task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
